import { useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import { Tag } from './ui'
import { LAB_REGISTRY } from './lab/registry'
import { LabPlaceholder } from './lab/LabPlaceholder'
import { useT } from '../lib/i18n'

interface LabPageProps {
  paradigmId: string
  onSelectParadigm: (id: string) => void
  onBackToParadigms: () => void
}

export function LabPage({ paradigmId, onSelectParadigm, onBackToParadigms }: LabPageProps) {
  const t = useT()
  const lab = t.lab
  const allParadigms = t.paradigms.paradigms

  const currentIdx = useMemo(() => {
    const idx = allParadigms.findIndex((p) => p.id === paradigmId)
    return idx >= 0 ? idx : 0
  }, [allParadigms, paradigmId])
  const current = allParadigms[currentIdx]
  const prev = allParadigms[(currentIdx - 1 + allParadigms.length) % allParadigms.length]
  const next = allParadigms[(currentIdx + 1) % allParadigms.length]
  const DemoComponent = LAB_REGISTRY[current.id] ?? LabPlaceholder

  // Scroll to top whenever the active paradigm changes.
  useEffect(() => {
    if (typeof window === 'undefined') return
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [current.id])

  return (
    <main className="mx-auto max-w-7xl px-6 sm:px-10 pt-12 sm:pt-16 pb-24">
      <header className="mb-10 flex flex-col gap-4 max-w-4xl">
        <div className="flex items-center gap-3 flex-wrap">
          <Tag kind="rule">{lab.sectionTag}</Tag>
          <button
            type="button"
            onClick={onBackToParadigms}
            className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)] hover:text-[color:var(--color-orion-text)] transition-colors"
          >
            {lab.backToParadigmsLabel}
          </button>
        </div>
        <h1
          className="text-[color:var(--color-orion-text)] leading-[1.1]"
          style={{ fontSize: 'var(--text-h1)', fontWeight: 600 }}
        >
          {lab.heroTitle}
        </h1>
        <p
          className="text-[color:var(--color-orion-haze)]"
          style={{ fontSize: 'var(--text-lead)', lineHeight: 1.6 }}
        >
          {lab.heroSubtitle}
        </p>
      </header>

      <div className="grid lg:grid-cols-[260px_1fr] gap-8">
        <aside className="lg:sticky lg:top-24 self-start">
          <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-amber)] mb-3">
            {lab.sidebarTitle}
          </div>
          <nav className="flex flex-col gap-0.5">
            {allParadigms.map((p) => {
              const active = p.id === current.id
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => onSelectParadigm(p.id)}
                  aria-current={active ? 'page' : undefined}
                  className={`group text-left px-3 py-2 rounded-[var(--radius-sm)] flex items-baseline gap-3 transition-colors ${
                    active
                      ? 'bg-[color:var(--color-orion-amber)]/15 border-l-2 border-[color:var(--color-orion-amber)]'
                      : 'border-l-2 border-transparent hover:bg-[color:var(--color-orion-night)]/60'
                  }`}
                >
                  <span
                    className={`mono text-[length:var(--text-micro)] shrink-0 ${
                      active ? 'text-[color:var(--color-orion-amber)]' : 'text-[color:var(--color-orion-haze)]'
                    }`}
                  >
                    {p.number}
                  </span>
                  <span
                    className={`text-[length:var(--text-small)] leading-snug ${
                      active
                        ? 'text-[color:var(--color-orion-text)] font-medium'
                        : 'text-[color:var(--color-orion-haze)] group-hover:text-[color:var(--color-orion-text)]'
                    }`}
                  >
                    {p.title}
                  </span>
                </button>
              )
            })}
          </nav>
        </aside>

        <motion.section
          key={current.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="min-w-0 space-y-6"
        >
          <DemoComponent paradigm={current} lab={lab} />

          <nav
            className="grid sm:grid-cols-2 gap-3 pt-6 border-t border-[color:var(--color-orion-surface)]/40"
            aria-label="Paradigm navigation"
          >
            <button
              type="button"
              onClick={() => onSelectParadigm(prev.id)}
              className="text-left p-4 rounded-[var(--radius-md)] border-faint hover:bg-[color:var(--color-orion-deep)]/40 transition-colors group"
            >
              <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)] mb-1 group-hover:text-[color:var(--color-orion-amber)] transition-colors">
                ← {prev.number}
              </div>
              <div
                className="text-[color:var(--color-orion-text)] leading-snug"
                style={{ fontSize: 'var(--text-small)' }}
              >
                {prev.title}
              </div>
            </button>
            <button
              type="button"
              onClick={() => onSelectParadigm(next.id)}
              className="text-right p-4 rounded-[var(--radius-md)] border-faint hover:bg-[color:var(--color-orion-deep)]/40 transition-colors group"
            >
              <div className="mono text-[length:var(--text-micro)] uppercase tracking-widest text-[color:var(--color-orion-haze)] mb-1 group-hover:text-[color:var(--color-orion-amber)] transition-colors">
                {next.number} →
              </div>
              <div
                className="text-[color:var(--color-orion-text)] leading-snug"
                style={{ fontSize: 'var(--text-small)' }}
              >
                {next.title}
              </div>
            </button>
          </nav>
        </motion.section>
      </div>
    </main>
  )
}
